import type {
  FactRaw,
  ConditionRaw,
  EventRaw,
  ActionRaw,
  RuleRaw,
  EventRuleRaw,
  StateRuleRaw,
  ShorthandConditions,
  EventRuleRawAppJSON,
  StateRuleRawAppJSON,
  RuleRawAppJSON,
  ActionRawAppJSON,
  EventRawAppJSON
} from "@certa/queries/schemas/studio/eca.schema";
import { getNanoId } from "@certa/studio-core";
import type {
  Fact,
  Condition,
  Event,
  Rule,
  EventRule,
  StateRule,
  SaveResponseAction,
  CreateChildWorkflowFromIntegrationResultAction,
  ScheduleIntegrationAction,
  CrossProcessSaveResponseAction,
  CreateChildWorkflowAction,
  SendNotificationAction,
  NameStepVersionAction,
  ChangeAccessAction,
  AssignStepAction,
  UpdateFieldAction,
  StepAction,
  StartWorkflowAction,
  Action,
  SyncDataObjectAction,
  UpdateStepLockAction,
  UpdateWorkflowV2Action,
  ScreenEntityAction,
  InviteThirdPartyUsersAction,
  TriggerHermesIntegrationAction,
  DeregisterScreeningRequestsAction,
  LinkDataObjectAction,
  BulkStatusUpdateDataObjectAction,
  GeneratePdfFromDocAction,
  ScreenRelatedWorkflowsAction,
  CreateChildWorkflowV2Action,
  SetObjectRelationshipAction,
  BulkLinkChildWorkflowsAction,
  UnsetObjectRelationshipAction,
  CreateSurveyAction,
  DelinkWorkflowAction,
  SetScreeningUpdateEventStatusAction,
  SendTestSurveyEmailAction
} from "@certa/studio-core/src/types/eca.types";
import { isEmpty } from "lodash-es";

const getShortFormFactData = (
  fact: unknown
): { type: "value" | "attribute" | "event"; value: string } | undefined => {
  if (typeof fact !== "string") return undefined;

  // Check if string matches the short form pattern: type::value
  const parts = fact.split("::");
  if (parts.length !== 2) throw new Error(`Invalid short form fact: ${fact}`);

  const [type, value] = parts;

  // Only value, attribute, and event types are supported in short form
  if (type === "value" || type === "attribute" || type === "event") {
    return { type, value: value?.trim() || "" };
  }
};

const createValueFactFromShortForm = (value: string): Fact => {
  if (value === "") return { type: "value", value: "" };

  if (value === "true") return { type: "value", value: true };
  if (value === "false") return { type: "value", value: false };
  if (value === "null") return { type: "value", value: null };

  // Try integer first
  const intVal = parseInt(value, 10);
  if (!isNaN(intVal) && String(intVal) === value) {
    return { type: "value", value: intVal };
  }

  // Try float
  const floatVal = parseFloat(value);
  if (!isNaN(floatVal) && String(floatVal) === value) {
    return { type: "value", value: floatVal };
  }

  // Fallback to string
  return { type: "value", value };
};

const createAttributeFactFromShortForm = (value: string): Fact => {
  const parts = value.split(".");
  const hasReverse = parts[parts.length - 1] === "reverse";

  if (hasReverse) {
    parts.pop(); // Remove 'reverse' from parts
  }

  const [objectType, ...rest] = parts;

  if (objectType === "workflow") {
    const [objectAttribute] = rest;
    return {
      type: "attribute",
      objectType,
      objectAttribute,
      ...(hasReverse && { reverse: true })
    };
  }

  const [objectTag, objectAttribute] = rest;
  return {
    type: "attribute",
    objectType: objectType as
      | "workflow"
      | "step"
      | "step_group"
      | "field"
      | "response"
      | undefined,
    objectTag,
    objectAttribute,
    ...(hasReverse && { reverse: true })
  };
};

const createEventFactFromShortForm = (value: string): Fact => {
  return {
    type: "event",
    value
  };
};

const createFact = (fact: FactRaw | string): Fact => {
  // check if fact is in short form
  const shortFormData = getShortFormFactData(fact);
  if (shortFormData) {
    const { type, value } = shortFormData;

    if (type === "value") {
      return createValueFactFromShortForm(value);
    }

    if (type === "attribute") {
      return createAttributeFactFromShortForm(value);
    }

    if (type === "event") {
      return createEventFactFromShortForm(value);
    }
  }
  return {
    type: fact.type,
    value: fact.value,
    objectType: fact.object_type,
    objectTag: fact.object_tag,
    objectAttribute: fact.object_attribute,
    reverse: fact.reverse,
    operation: fact.operation,
    variables: fact.variables?.map(createFact),
    options: fact.options
      ? {
          weights: fact.options.weights,
          delimiter: fact.options.delimiter,
          start: fact.options.start,
          replaceString: fact.options.replace_string,
          replaceWith: fact.options.replace_with,
          index: fact.options.index,
          diffParam: fact.options.diff_param,
          defaultValue: fact.options.default_value,
          fromFormat: fact.options.from_format,
          valuesToReplace: fact.options.values_to_replace,
          units: fact.options.units,
          subtract: fact.options.subtract,
          precision: fact.options.precision,
          end: fact.options.end
        }
      : undefined,
    source: fact.source ? createFact(fact.source) : undefined,
    query: fact.query,
    dataAttribute: fact?.data_attribute,
    relationPath: fact?.relation_path,
    settingsType: fact.settings_type,
    settingsName: fact.settings_name,
    clauses: fact.clauses?.map(
      (clause: { condition: ConditionRaw; then: FactRaw }) => ({
        condition: createCondition(clause.condition),
        then: clause?.then
          ? createFact(clause.then)
          : { type: "value", value: true }
      })
    ),
    default: fact.clauses?.length
      ? fact.default
        ? createFact(fact.default)
        : { type: "value", value: false }
      : undefined,
    extraJsonTags: fact.extra_json_tags,
    fetchIntegrationJsonForFields: fact.fetch_integration_json_for_fields,
    userIdentifier: fact.user_identifier
      ? createFact(fact.user_identifier)
      : undefined,
    userEmail: fact.user_email ? createFact(fact.user_email) : undefined,
    attribute: fact.attribute,
    tag: fact.tag,
    dataPath: fact.data_path
  };
};

const createCondition = (condition: ConditionRaw): Condition => ({
  operator: condition.operator,
  left: condition.left ? createFact(condition.left) : undefined,
  right: condition.right ? createFact(condition.right) : undefined,
  any: condition.any?.map(createCondition),
  all: condition.all?.map(createCondition)
});

const createEvent = (event: EventRaw | EventRawAppJSON): Event => {
  const __studioId =
    ("__studio_id" in event ? event.__studio_id : getNanoId()) ?? getNanoId();
  switch (event.name) {
    case "create_workflow":
    case "adjudicate_workflow":
    case "change_third_party_user_access": {
      return {
        __studioId,
        name: event.name
      };
    }
    case "archive_workflow": {
      return {
        __studioId,
        name: event.name,
        extra: {
          sourceKind: event.extra.source_kind
        }
      };
    }
    case "submit_step":
    case "undo_step":
    case "initiate_step":
    case "save_response":
    case "receive_integration":
    case "select_integration_result":
    case "adjudicate_integration_result":
    case "manual_integration_trigger":
    case "screening_initiated": {
      return {
        __studioId,
        name: event.name,
        onTag: event.on_tag
      };
    }
    case "create_object":
    case "update_object": {
      return {
        __studioId,
        name: event.name,
        extra: {
          sourceKind: event.extra.source_kind,
          objectType: event.extra.object_type,
          objectReference: event.extra.object_reference
        }
      };
    }
    default: {
      // @ts-expect-error - This will not have error if you missed adding a case
      // having error = all known cases are handled.
      throw new Error(`Unknown event name: ${event.name}`);
    }
  }
};

const createAction = (action: ActionRaw | ActionRawAppJSON): Action => {
  const __studioId =
    ("__studio_id" in action ? action.__studio_id : getNanoId()) ?? getNanoId();
  switch (action.name) {
    case "save_response": {
      const saveResponseAction: SaveResponseAction = {
        __studioId,
        name: "save_response",
        params: {
          tag: action.params.tag,
          attr: action.params.attr,
          value: createFact(action.params.value),
          aggregateBy:
            action.params.aggregate_by === ""
              ? undefined
              : action.params.aggregate_by,
          length: action.params.length
        },
        order: action.order
      };
      return saveResponseAction;
    }

    case "update_field": {
      const updateFieldAction: UpdateFieldAction = {
        __studioId,
        name: "update_field",
        params: {
          tag: action.params.tag,
          isEnabled: action.params.is_enabled
            ? createFact(action.params.is_enabled)
            : undefined,
          isRequired: action.params.is_required
            ? createFact(action.params.is_required)
            : undefined,
          isReadOnly: action.params.is_read_only
            ? createFact(action.params.is_read_only)
            : undefined,
          regexValue: action.params.regex_value,
          regexError: action.params.regex_error,
          statusIndicator: action.params.status_indicator
        },
        order: action.order
      };
      return updateFieldAction;
    }

    case "change_access": {
      const common = {
        staticGroupsToAdd: action.params.static_groups_to_add,
        staticGroupsToRemove: action.params.static_groups_to_remove,
        dynamicGroupsToAdd: action.params.dynamic_groups_to_add,
        dynamicGroupsToRemove: action.params.dynamic_groups_to_remove
      };
      const changeAccessAction: ChangeAccessAction = {
        __studioId,
        name: "change_access",
        params:
          action.params.object_type === "workflow"
            ? {
                objectType: action.params.object_type,
                ...common
              }
            : {
                objectType: action.params.object_type,
                objectTag: action.params.object_tag,
                ...common
              },
        order: action.order
      };
      return changeAccessAction;
    }

    case "submit_step":
    case "undo_step":
    case "enable_step":
    case "disable_step": {
      const stepAction: StepAction = {
        __studioId,
        name: action.name,
        params: {
          tag: action.params.tag
        },
        order: action.order
      };
      return stepAction;
    }

    case "name_step_version": {
      const nameStepVersionAction: NameStepVersionAction = {
        __studioId,
        name: "name_step_version",
        params: {
          tag: action.params.tag,
          label: action.params.label,
          color: action.params.color
        },
        order: action.order
      };
      return nameStepVersionAction;
    }

    case "update_workflow_v2": {
      const updateWorkflowAction: UpdateWorkflowV2Action = {
        __studioId,
        name: "update_workflow_v2",
        params: {
          name: action.params.name ? createFact(action.params.name) : undefined,
          lcId: action.params.lc_id
            ? createFact(action.params.lc_id)
            : undefined,
          lcMessage: action.params.lc_message
            ? createFact(action.params.lc_message)
            : undefined,
          lcDueDate: action.params.lc_due_date
            ? createFact(action.params.lc_due_date)
            : undefined,
          externalId: action.params.external_id
            ? createFact(action.params.external_id)
            : undefined,
          lcData: action.params.lc_data
            ? Object.fromEntries(
                Object.entries(action.params.lc_data).map(([k, v]) => [
                  k,
                  createFact(v)
                ])
              )
            : undefined,
          logo: action.params.logo ? createFact(action.params.logo) : undefined,
          status: action.params.status
            ? createFact(action.params.status)
            : undefined,
          businessUnit: action.params.business_unit
            ? createFact(action.params.business_unit)
            : undefined,
          region: action.params.region
            ? createFact(action.params.region)
            : undefined
        },
        order: action.order
      };
      return updateWorkflowAction;
    }

    case "send_notification": {
      const sendNotificationAction: SendNotificationAction = {
        __studioId,
        name: "send_notification",
        params: {
          template: action.params.template,
          toEmails: createFact(action.params.to_emails),
          ccEmails: action.params.cc_emails
            ? createFact(action.params.cc_emails)
            : undefined,
          bccEmails: action.params.bcc_emails
            ? createFact(action.params.bcc_emails)
            : undefined,
          replyToEmail: action.params.reply_to_email
            ? createFact(action.params.reply_to_email)
            : undefined,
          replyToField: action.params.reply_to_field,
          stepsToInclude: action.params.steps_to_include,
          dataObjectReference: action.params.data_object_reference,
          language: action.params.language
            ? createFact(action.params.language)
            : undefined
        },
        order: action.order
      };
      return sendNotificationAction;
    }

    case "cross_process_save_response": {
      const crossProcessSaveResponseAction: CrossProcessSaveResponseAction = {
        __studioId,
        name: "cross_process_save_response",
        params: {
          relatedProcesses: {
            level: action.params.related_processes.level,
            kind: action.params.related_processes.kind
          },
          source: createFact(action.params.source),
          target: {
            tag: action.params.target.tag,
            attr: action.params.target.attr
          },
          aggregationPolicy: action.params.aggregation_policy,
          length: action.params.length
        },
        order: action.order
      };
      return crossProcessSaveResponseAction;
    }

    case "create_child_workflow": {
      const createChildWorkflowAction: CreateChildWorkflowAction = {
        __studioId,
        name: "create_child_workflow",
        params: {
          name: createFact(action.params.name),
          kind: action.params.kind,
          singleFieldValues: action.params.single_field_values
            ? Object.fromEntries(
                Object.entries(action.params.single_field_values).map(
                  ([k, v]) => [k, createFact(v)]
                )
              )
            : undefined,
          indexedFieldValues: action.params.indexed_field_values
            ? Object.fromEntries(
                Object.entries(action.params.indexed_field_values).map(
                  ([k, v]) => [k, createFact(v)]
                )
              )
            : undefined
        },
        order: action.order
      };
      return createChildWorkflowAction;
    }

    case "schedule_integration": {
      const scheduleIntegrationAction: ScheduleIntegrationAction = {
        __studioId,
        name: "schedule_integration",
        params: {
          tag: action.params.tag,
          apiKeyMap: action.params.api_key_map
            ? Object.fromEntries(
                Object.entries(action.params.api_key_map).map(([k, v]) => [
                  k,
                  createFact(v)
                ])
              )
            : undefined,
          poll: action.params.poll,
          pollingInterval: action.params.polling_interval
        },
        order: action.order
      };
      return scheduleIntegrationAction;
    }

    case "create_child_workflow_from_integration_result": {
      const createChildWorkflowFromIntegrationResultAction: CreateChildWorkflowFromIntegrationResultAction =
        {
          __studioId,
          name: "create_child_workflow_from_integration_result",
          params: {
            tag: action.params.tag
          },
          order: action.order
        };
      return createChildWorkflowFromIntegrationResultAction;
    }

    case "assign_step": {
      const assignStepAction: AssignStepAction = {
        __studioId,
        name: "assign_step",
        params: {
          tag: action.params.tag,
          users: action.params.users.map(createFact),
          userTag: action.params.user_tag
        },
        order: action.order
      };
      return assignStepAction;
    }

    case "start_workflow": {
      const startWorkflowAction: StartWorkflowAction = {
        __studioId,
        name: "start_workflow",
        params: {
          kind: action.params.kind,
          name: createFact(action.params.name),
          fieldValues: Object.fromEntries(
            Object.entries(action.params.field_values).map(([k, v]) => [
              k,
              createFact(v)
            ])
          ),
          dataObjectReferenceToObjects: action.params
            .data_object_reference_to_objects
            ? Object.fromEntries(
                Object.entries(
                  action.params.data_object_reference_to_objects
                ).map(([k, v]) => [k, createFact(v)])
              )
            : undefined
        },
        order: action.order
      };
      return startWorkflowAction;
    }

    case "sync_data_object": {
      const syncDataObjectAction: SyncDataObjectAction = {
        __studioId,
        name: "sync_data_object",
        params: {
          dataObjectType: action.params.data_object_type,
          dataObjectName: createFact(action.params.data_object_name),
          dataMapping: action.params.data_mapping?.map(mapping => ({
            dataObjectAttribute: mapping.data_object_attribute,
            workflowAttribute: createFact(mapping.workflow_attribute),
            operation: mapping.operation
          })),
          dataObjectReference: action.params.data_object_reference,
          setLogo: action.params.set_logo,
          dynamicGroupRoleMapping: action.params.dynamic_group_role_mapping,
          dataObjectStatus: action.params.data_object_status,
          sharableGroupsInHierarchy: action.params.sharable_groups_in_hierarchy,
          duplicateCheckContext: action.params.duplicate_check_context
            ? {
                parentObjectReference:
                  action.params.duplicate_check_context.parent_object_reference,
                relationPath:
                  action.params.duplicate_check_context.relation_path,
                objectStatus:
                  action.params.duplicate_check_context.object_status
              }
            : undefined,
          saveObjectCreatedFlagToField:
            action.params.save_object_created_flag_to_field
        },
        order: action.order
      };
      return syncDataObjectAction;
    }

    case "update_step_lock": {
      const updateStepLockAction: UpdateStepLockAction = {
        __studioId,
        name: "update_step_lock",
        params: {
          tag: action.params.tag,
          isLocked: createFact(action.params.is_locked)
        },
        order: action.order
      };
      return updateStepLockAction;
    }

    case "screen_entity": {
      const screenEntityAction: ScreenEntityAction = {
        __studioId,
        name: "screen_entity",
        params: {
          tag: action.params.tag,
          provider: action.params.provider,
          name: createFact(action.params.name),
          requestData: action.params.request_data
            ? Object.fromEntries(
                Object.entries(action.params.request_data).map(([k, v]) => [
                  k,
                  createFact(v)
                ])
              )
            : undefined,
          skipIndexDistributionKeys: action.params.skip_index_distribution_keys,
          dedupeConfig: action.params.dedupe_config
        },
        order: action.order
      };
      return screenEntityAction;
    }

    case "invite_third_party_users": {
      const inviteThirdPartyUsersAction: InviteThirdPartyUsersAction = {
        __studioId,
        name: "invite_third_party_users",
        params: {
          emails: action.params.emails.map(createFact),
          dynamicGroupNames: action.params.dynamic_group_names
        },
        order: action.order
      };
      return inviteThirdPartyUsersAction;
    }

    case "trigger_hermes_integration": {
      const triggerHermesIntegrationAction: TriggerHermesIntegrationAction = {
        __studioId,
        name: "trigger_hermes_integration",
        params: {
          tag: action.params.tag,
          payloadMap: action.params.payload_map
        },
        order: action.order
      };
      return triggerHermesIntegrationAction;
    }

    case "deregister_screening_requests": {
      const deregisterScreeningRequestsAction: DeregisterScreeningRequestsAction =
        {
          __studioId,
          name: "deregister_screening_requests",
          params: {
            tag: action.params.tag
          },
          order: action.order
        };
      return deregisterScreeningRequestsAction;
    }

    case "link_data_object": {
      const dataObjectReferences = action.params.data_object_references
        ? Object.fromEntries(
            Object.entries(action.params.data_object_references).map(
              ([key, value]) => [
                key,
                {
                  type: "data_object_reference" as const,
                  value: value.value,
                  relationPath: value.relation_path
                }
              ]
            )
          )
        : {};

      const linkDataObjectAction: LinkDataObjectAction = {
        __studioId,
        name: "link_data_object",
        params: {
          dataObjectReferences
        },
        order: action.order
      };
      return linkDataObjectAction;
    }

    case "bulk_status_update_data_object": {
      const bulkStatusUpdateDataObjectAction: BulkStatusUpdateDataObjectAction =
        {
          __studioId,
          name: "bulk_status_update_data_object",
          params: {
            dataObjectReference:
              action.params.data_object_reference || undefined,
            relPathsStatusMap: action.params.rel_paths_status_map || undefined,
            objectIds: action.params.object_ids
              ? createFact(action.params.object_ids)
              : undefined,
            status: action.params.status
          },
          order: action.order
        };
      return bulkStatusUpdateDataObjectAction;
    }

    case "generate_pdf_from_doc": {
      const generatePdfFromDocAction: GeneratePdfFromDocAction = {
        __studioId,
        name: "generate_pdf_from_doc",
        params: {
          nameFormat: action.params.name_format,
          targetFieldTag: action.params.target_field_tag,
          versionFieldTag: action.params.version_field_tag,
          renderDocumentUrl: action.params.render_document_url,
          contextTemplateSlug: action.params.context_template_slug
        },
        order: action.order
      };
      return generatePdfFromDocAction;
    }

    case "screen_related_workflows": {
      const screenRelatedWorkflowsAction: ScreenRelatedWorkflowsAction = {
        __studioId,
        name: "screen_related_workflows",
        params: {
          screeningFieldTag: action.params.screening_field_tag,
          provider: action.params.provider,
          relatedWorkflowMappings: action.params.related_workflow_mappings.map(
            mapping => ({
              childKind: mapping.child_kind,
              shouldScreenField: mapping.should_screen_field,
              searchRequestIdField: mapping.search_request_id_field,
              searchRequestNameField: mapping.search_request_name_field,
              requestDataMapping: mapping.request_data_mapping
            })
          ),
          dedupeConfig: action.params?.dedupe_config
            ? {
                required: action.params.dedupe_config?.required,
                dedupeAgainstParentObject:
                  action.params.dedupe_config?.dedupe_against_parent_object,
                parentObjectReference:
                  action.params.dedupe_config?.parent_object_reference
              }
            : undefined
        },
        order: action.order
      };
      return screenRelatedWorkflowsAction;
    }

    case "create_child_workflow_v2": {
      const createChildWorkflowV2Action: CreateChildWorkflowV2Action = {
        __studioId,
        name: "create_child_workflow_v2",
        params: {
          kind: action.params.kind,
          name: {
            type: action.params.name.type,
            fact: createFact(action.params.name.fact)
          },
          fieldValues: Object.fromEntries(
            Object.entries(action.params.field_values).map(([k, v]) => [
              k,
              {
                type: v.type,
                fact: createFact(v.fact)
              }
            ])
          ),
          duplicateCheck: action.params.duplicate_check
            ? {
                checkAcross: action.params.duplicate_check.check_across,
                fuzzyThreshold: action.params.duplicate_check.fuzzy_threshold,
                updateDuplicateWorkflow:
                  action.params.duplicate_check.update_duplicate_workflow,
                updateDuplicateWorkflowAnswers:
                  action.params.duplicate_check
                    .update_duplicate_workflow_answers,
                filterLogic: action.params.duplicate_check.filter_logic
                  ? {
                      operator:
                        action.params.duplicate_check.filter_logic.operator,
                      conditions: action.params.duplicate_check.filter_logic
                        .conditions
                        ? action.params.duplicate_check.filter_logic.conditions.map(
                            c => ({
                              field: c.field,
                              matchType: c.match_type
                            })
                          )
                        : undefined
                    }
                  : undefined
              }
            : undefined
        },
        order: action.order
      };
      return createChildWorkflowV2Action;
    }

    case "set_object_relationship": {
      const setObjectRelationshipAction: SetObjectRelationshipAction = {
        __studioId,
        name: "set_object_relationship",
        params: {
          object: {
            type: action.params.object.type,
            value: action.params.object.value,
            relationPath: action.params.object.relation_path
          },
          toLinkObjects: {
            workflows: {
              kind: action.params.to_link_objects.workflows.kind,
              level: action.params.to_link_objects.workflows.level
            },
            objectReference: action.params.to_link_objects.object_reference
          },
          relationName: action.params.relation_name,
          reverseRelationship: action.params.reverse_relationship
        },
        order: action.order
      };
      return setObjectRelationshipAction;
    }
    case "bulk_link_child_workflows": {
      const bulkLinkChildWorkflowsAction: BulkLinkChildWorkflowsAction = {
        __studioId,
        name: "bulk_link_child_workflows",
        params: {
          commonParent: {
            kind: action.params.common_parent.kind,
            level: action.params.common_parent.level
          },
          kindTag: action.params.kind_tag,
          relationType: action.params.relation_type
        },
        order: action.order
      };
      return bulkLinkChildWorkflowsAction;
    }
    case "unset_object_relationship": {
      const unsetObjectRelationshipAction: UnsetObjectRelationshipAction = {
        __studioId,
        name: "unset_object_relationship",
        params: {
          dataObject: {
            type: action.params.data_object.type,
            value: action.params.data_object.value,
            relationPath: action.params.data_object.relation_path
          },
          relationName: action.params.relation_name,
          objectToUnset: createFact(action.params.object_to_unset)
        },
        order: action.order
      };
      return unsetObjectRelationshipAction;
    }

    case "create_survey": {
      const createSurveyAction: CreateSurveyAction = {
        __studioId,
        name: "create_survey",
        params: {
          tag: action.params.tag
        },
        order: action.order
      };
      return createSurveyAction;
    }
    case "delink_workflow": {
      const delinkWorkflowAction: DelinkWorkflowAction = {
        __studioId,
        name: "delink_workflow",
        params: {
          relationType: action.params.relation_type,
          workflow: createFact(action.params.workflow)
        },
        order: action.order
      };
      return delinkWorkflowAction;
    }

    case "set_screening_update_event_status": {
      const setScreeningUpdateEventStatusAction: SetScreeningUpdateEventStatusAction =
        {
          __studioId,
          name: "set_screening_update_event_status",
          params: {
            tag: action.params.tag,
            status: action.params.status
          },
          order: action.order
        };
      return setScreeningUpdateEventStatusAction;
    }

    case "send_test_survey_email": {
      const sendTestSurveyEmailAction: SendTestSurveyEmailAction = {
        __studioId,
        name: "send_test_survey_email",
        params: {
          kindTag: createFact(action.params.kind_tag),
          recipientEmail: createFact(action.params.recipient_email)
        },
        order: action.order
      };
      return sendTestSurveyEmailAction;
    }

    default: {
      // @ts-expect-error - This will not have error if you missed adding a case
      // having error = all known cases are handled.
      throw new Error(`Unknown action: ${action.name}`);
    }
  }
};

const createConditionFromShorthand = (
  shorthandConditions: ShorthandConditions
): Condition => {
  if (shorthandConditions.all_fields_have_same_response) {
    const fields = shorthandConditions.all_fields_have_same_response;
    return {
      all: fields.slice(0, -1).map((field, index) => ({
        left: {
          type: "attribute",
          objectType: "response",
          objectTag: field,
          objectAttribute: "answer"
        },
        operator: "eq",
        right: {
          type: "attribute",
          objectType: "response",
          objectTag: fields[index + 1],
          objectAttribute: "answer"
        }
      }))
    };
  }

  if (shorthandConditions.all_fields_have_matching_response) {
    const { tags, value } =
      shorthandConditions.all_fields_have_matching_response;
    return {
      all: tags.map(tag => ({
        left: {
          type: "attribute",
          objectType: "response",
          objectTag: tag,
          objectAttribute: "answer"
        },
        operator: "eq",
        right: createFact(value)
      }))
    };
  }

  if (shorthandConditions.any_field_have_matching_response) {
    const { tags, value } =
      shorthandConditions.any_field_have_matching_response;
    return {
      any: tags.map(tag => ({
        left: {
          type: "attribute",
          objectType: "response",
          objectTag: tag,
          objectAttribute: "answer"
        },
        operator: "eq",
        right: createFact(value)
      }))
    };
  }

  if (shorthandConditions.all_field_responses_empty) {
    const fields = shorthandConditions.all_field_responses_empty;
    return {
      all: fields.map(field => ({
        left: {
          type: "attribute",
          objectType: "response",
          objectTag: field,
          objectAttribute: "answer"
        },
        operator: "eq",
        right: {
          type: "value",
          value: ""
        }
      }))
    };
  }

  if (shorthandConditions.any_field_responses_empty) {
    const fields = shorthandConditions.any_field_responses_empty;
    return {
      any: fields.map(field => ({
        left: {
          type: "attribute",
          objectType: "response",
          objectTag: field,
          objectAttribute: "answer"
        },
        operator: "eq",
        right: {
          type: "value",
          value: ""
        }
      }))
    };
  }

  if (shorthandConditions.no_field_responses_empty) {
    const fields = shorthandConditions.no_field_responses_empty;
    return {
      all: fields.map(field => ({
        left: {
          type: "attribute",
          objectType: "response",
          objectTag: field,
          objectAttribute: "answer"
        },
        operator: "is_not",
        right: {
          type: "value",
          value: ""
        }
      }))
    };
  }

  throw new Error(`Unknown shorthand condition type`);
};

const createRule = (rule: RuleRaw | RuleRawAppJSON): Rule => {
  const __studioId =
    ("__studio_id" in rule ? rule.__studio_id : getNanoId()) ?? getNanoId();
  // Check if rule has shorthand conditions
  if (rule.shorthand_conditions) {
    return {
      __studioId,
      name: rule.name,
      conditions: createConditionFromShorthand(rule.shorthand_conditions),
      actions: rule.actions.map(createAction)
    };
  }

  // Existing logic for regular conditions
  return {
    __studioId,
    name: rule.name,
    conditions:
      rule.conditions && !isEmpty(rule.conditions)
        ? createCondition(rule.conditions)
        : undefined,
    actions: rule.actions.map(createAction)
  };
};

const createEventRule = (
  eventRuleRaw: EventRuleRaw | EventRuleRawAppJSON
): EventRule => {
  const __studioId =
    ("__studio_id" in eventRuleRaw ? eventRuleRaw.__studio_id : getNanoId()) ??
    getNanoId();
  const eventRule: EventRule = {
    __studioId,
    rules: eventRuleRaw.rules.map(createRule),
    events: eventRuleRaw.events.map(createEvent),
    __modifiedAt: Date.now(),
    __createdAt: Date.now()
  };

  return eventRule;
};

const createStateRule = (
  stateRule: StateRuleRaw | StateRuleRawAppJSON
): StateRule => {
  const __studioId =
    ("__studio_id" in stateRule ? stateRule.__studio_id : getNanoId()) ??
    getNanoId();
  switch (stateRule.name) {
    case "save_response":
      return {
        __studioId,
        name: "save_response",
        params: {
          tag: stateRule.params.tag,
          attr: stateRule.params.attr,
          value: createFact(stateRule.params.value),
          aggregateBy: stateRule.params.aggregate_by,
          length: stateRule.params.length
        },
        __modifiedAt: Date.now(),
        __createdAt: Date.now()
      };
    case "update_field":
      return {
        __studioId,
        name: "update_field",
        params: {
          tag: stateRule.params.tag,
          isEnabled: stateRule.params.is_enabled
            ? createFact(stateRule.params.is_enabled)
            : undefined,
          isRequired: stateRule.params.is_required
            ? createFact(stateRule.params.is_required)
            : undefined,
          isReadOnly: stateRule.params.is_read_only
            ? createFact(stateRule.params.is_read_only)
            : undefined,
          regexValue: stateRule.params.regex_value,
          regexError: stateRule.params.regex_error,
          statusIndicator: stateRule.params.status_indicator
        },
        __modifiedAt: Date.now(),
        __createdAt: Date.now()
      };
    case "update_workflow_v2":
      return {
        __studioId,
        name: "update_workflow_v2",
        params: {
          name: stateRule.params.name
            ? createFact(stateRule.params.name)
            : undefined,
          lcId: stateRule.params.lc_id
            ? createFact(stateRule.params.lc_id)
            : undefined,
          lcMessage: stateRule.params.lc_message
            ? createFact(stateRule.params.lc_message)
            : undefined,
          lcDueDate: stateRule.params.lc_due_date
            ? createFact(stateRule.params.lc_due_date)
            : undefined,
          externalId: stateRule.params.external_id
            ? createFact(stateRule.params.external_id)
            : undefined,
          lcData: stateRule.params.lc_data
            ? Object.fromEntries(
                Object.entries(stateRule.params.lc_data).map(([k, v]) => [
                  k,
                  createFact(v)
                ])
              )
            : undefined,
          logo: stateRule.params.logo
            ? createFact(stateRule.params.logo)
            : undefined,
          status: stateRule.params.status
            ? createFact(stateRule.params.status)
            : undefined,
          businessUnit: stateRule.params.business_unit
            ? createFact(stateRule.params.business_unit)
            : undefined,
          region: stateRule.params.region
            ? createFact(stateRule.params.region)
            : undefined
        },
        __modifiedAt: Date.now(),
        __createdAt: Date.now()
      };
    default:
      return stateRule as StateRule;
  }
};

export {
  createFact,
  createCondition,
  createEvent,
  createAction,
  createRule,
  createEventRule,
  createStateRule
};
